/**
 * Payments bridge: in-app purchases from the web client when running inside the Flutter app.
 *
 * Flow: Web calls requestFlutterPurchase(productId) → Flutter opens native IAP (StoreKit/Play Billing)
 * → Flutter calls backend validatePurchase with receipt/token → backend writes to Firestore
 * → Flutter returns result to WebView. See docs/PAYMENTS.md for full architecture.
 *
 * Uses the same window.flutter_inappwebview type as useFlutterFacebookAuth / useFlutterGoogleAuth.
 */

import { logIapDebugEvent } from '../utils/iapDebugLog';

export interface PurchaseResult {
  success: boolean;
  error?: string;
  productId?: string;
  subscriptionType?: string;
  /** ISO date string when the subscription expires (from validatePurchase response) */
  expiresAt?: string;
  /** Product IDs the store confirmed exist, from Flutter's queryProductDetails() just before purchase */
  foundProductIds?: string[];
  /** Product IDs the store could not find, from Flutter's queryProductDetails() just before purchase */
  notFoundProductIds?: string[];
}

/**
 * Check if running inside Flutter WebView (in-app purchase available).
 */
export const useIsFlutterWebView = (): boolean => {
  return typeof window !== 'undefined' && window.flutter_inappwebview !== undefined;
};

/**
 * Request in-app purchase from Flutter. Flutter will open native IAP, then validate receipt with server.
 * Pass userId so the server stores the subscription with this user (from onboarding login).
 * @param productId - One of 'learn_parasha' | 'learn_any_monthly'
 * @param userId - Optional; if not provided, read from localStorage loggedInUser.uid or .email
 * @returns PurchaseResult with success and optional error/subscriptionType
 */
export const requestFlutterPurchase = async (
  productId: string,
  userId?: string | null,
  basePlanId?: string | null,
  offerId?: string | null,
): Promise<PurchaseResult> => {
  try {
    if (!window.flutter_inappwebview) {
      return {
        success: false,
        error: 'Not running in app. In-app purchases are only available in the Kol Torah app.',
      };
    }

    let effectiveUserId: string | null = null;
    let effectiveUserEmail: string | null = null;
    try {
      const raw = localStorage.getItem('loggedInUser');
      if (raw) {
        const parsed = JSON.parse(raw) as { userId?: string; uid?: string; email?: string };
        effectiveUserId = userId ?? parsed.userId ?? parsed.uid ?? parsed.email ?? null;
        effectiveUserEmail = parsed.email ?? null;
      } else if (userId) {
        effectiveUserId = userId;
      }
    } catch {
      /* ignore */
    }

    console.log('[Subscriptions] requestPurchase: passing to Flutter', {
      productId,
      hasUserId: !!effectiveUserId,
      hasEmail: !!effectiveUserEmail,
      basePlanId: basePlanId ?? undefined,
      offerId: offerId ?? undefined,
    });

    // Built once and reused for both the actual Flutter call and the debug log,
    // so the log can never drift from what was really sent.
    const flutterHandlerName = 'requestPurchase';
    const flutterHandlerPayload = {
      productId,
      userId: effectiveUserId ?? undefined,
      email: effectiveUserEmail ?? undefined,
      ...(basePlanId ? { basePlanId } : {}),
      ...(offerId ? { offerId } : {}),
    };

    void logIapDebugEvent('requested', {
      flutterHandler: flutterHandlerName,
      flutterHandlerPayload,
    });

    const response = (await window.flutter_inappwebview.callHandler(
      flutterHandlerName,
      flutterHandlerPayload,
    )) as PurchaseResult | null | undefined;

    if (response?.success) {
      void logIapDebugEvent('result', {
        productId: response.productId ?? productId,
        success: true,
        subscriptionType: response.subscriptionType ?? null,
        expiresAt: response.expiresAt ?? null,
        foundProductIds: response.foundProductIds ?? null,
        notFoundProductIds: response.notFoundProductIds ?? null,
      });
      return {
        success: true,
        productId: response.productId ?? productId,
        subscriptionType: response.subscriptionType,
        expiresAt: response.expiresAt,
      };
    }

    const errorMessage =
      (response as { error?: string })?.error ?? 'Purchase failed or was cancelled';
    void logIapDebugEvent('result', {
      productId,
      success: false,
      error: errorMessage,
      foundProductIds: response?.foundProductIds ?? null,
      notFoundProductIds: response?.notFoundProductIds ?? null,
    });
    return {
      success: false,
      error: errorMessage,
      productId,
    };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    void logIapDebugEvent('result', {
      productId,
      success: false,
      error: message,
    });
    return { success: false, error: message, productId };
  }
};
